import {
    useState,
    useEffect,
    useRef,
    useLayoutEffect,
    type ChangeEvent,
} from "react";
import {ActionIcon, Tooltip} from "@mantine/core";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import DeleteSvg from "../../../../assets/v2-bin.svg?react";
import {useChecklistData} from "../ChecklistDataContext";
import {useComment} from "./CommentContext";
import {dibAppApi} from "../../../../dibAppApi/dibAppApi";
import styles from "./comment-panel.module.css";

const dateFormatter = new Intl.DateTimeFormat("nb", {dateStyle: "medium"});
const formatDate = (d: string) => {
    try {
        return dateFormatter.format(new Date(d));
    } catch {
        return d;
    }
};

const getInitials = (name: string) => {
    const parts = name.split(" ").filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0][0];
    return parts[0][0] + parts[parts.length - 1][0];
};

export const CommentGroup = ({id, title}: {id: string; title: string}) => {
    const {t} = useTranslation();
    const {checklistConfig, collectionData} = useChecklistData();
    const topicId = checklistConfig.metadata.topicId;
    const collectionId = collectionData?.metadata?.collectionId ?? "";

    const {focusedNode, setFocusedNode} = useComment();

    const {data: commentsData} = dibAppApi.endpoints.getComments.useQuery(
        {collectionId},
        {skip: !collectionId},
    );

    const [deleteComment, deleteResult] =
        dibAppApi.endpoints.deleteComment.useMutation();
    const [addComment] = dibAppApi.endpoints.addComment.useMutation();
    const [updateComment] = dibAppApi.endpoints.updateComment.useMutation();

    const commentObjId = commentsData?.elementdata?.[id]?.ci_comments?.[0];
    const comment = commentObjId
        ? commentsData?.objects?.[commentObjId]
        : undefined;
    const serverText = comment?.data?.comment ?? "";

    const isFocused = focusedNode?.id === id;

    const [value, setValue] = useState(serverText);
    // What the server holds, or is about to hold, for this item. Saving is a
    // no-op while the textarea matches it, so blur may fire as often as it
    // likes without re-sending.
    const savedValue = useRef(serverText);
    const deleteRequested = useRef(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Focus textarea when this item is focused
    useEffect(() => {
        if (isFocused && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isFocused]);

    // Auto-resize textarea
    useLayoutEffect(() => {
        setTimeout(() => {
            const el = textareaRef.current;
            if (el && el.scrollHeight > el.getBoundingClientRect().height) {
                el.style.height = `${el.scrollHeight}px`;
            }
        }, 0);
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
    };

    const showSaveError = () => {
        notifications.show({
            message: t("Error.SomethingWentWrong"),
            color: "red",
        });
    };

    // Add vs. update follows the cache, not the "new" flag the panel was
    // opened with: the backend's add is a MERGE on (collection, item), so a
    // POST for an item that already has a comment overwrites it.
    const save = () => {
        if (deleteRequested.current || value === savedValue.current) return;
        const previous = savedValue.current;
        savedValue.current = value;
        const request = commentObjId
            ? updateComment({
                  collectionId,
                  commentId: commentObjId,
                  comment: value,
              })
            : addComment({topicId, collectionId, id, comment: value});
        request.unwrap().catch(() => {
            // The user deleted the comment while this save was in flight; its
            // outcome no longer matters.
            if (deleteRequested.current) return;
            savedValue.current = previous;
            showSaveError();
        });
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!commentObjId) return;
        deleteRequested.current = true;
        setValue("");
        savedValue.current = "";
        // Drop the focus so the group unmounts as soon as the refetch
        // confirms the comment is gone (legacy behaviour). The panel stays
        // open.
        setFocusedNode(undefined, false);
        deleteComment({collectionId, commentId: commentObjId})
            .unwrap()
            .catch(() => {
                deleteRequested.current = false;
                savedValue.current = serverText;
                setValue(serverText);
                showSaveError();
            });
    };

    const scrollToItem = () => {
        document
            .querySelector(`[data-id="${id}"]`)
            ?.scrollIntoView({behavior: "auto", block: "start"});
    };

    // Guard: skip if no comment data and not focused
    if (!commentsData?.elementdata?.[id] && !isFocused) return null;

    const name = comment?.data?.editedBy ?? "";

    return (
        <li>
            <section
                className={`${styles.commentGroup}${isFocused ? ` ${styles.focused}` : ""}`}
            >
                <a
                    href={`#${id}`}
                    className={styles.commentTitle}
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToItem();
                    }}
                    title={title}
                >
                    {title || t("Word.Untitled")}
                </a>

                <div className={styles.commentItem}>
                    {name && (
                        <div className={styles.initials}>
                            {getInitials(name)}
                        </div>
                    )}
                    <div className={styles.commentWrapper}>
                        {comment && (
                            <div className={styles.commentMeta}>
                                <span>
                                    {comment.data?.editedBy && (
                                        <span className={styles.username}>
                                            {comment.data.editedBy}
                                        </span>
                                    )}
                                    {comment.data?.editDate && (
                                        <span className={styles.timestamp}>
                                            {formatDate(
                                                comment.data.editDate,
                                            )}
                                        </span>
                                    )}
                                </span>
                                {comment.data?.editedBy && (
                                    <Tooltip label={t("Action.DeleteComment")}>
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            size="sm"
                                            aria-label={t(
                                                "Action.DeleteComment",
                                            )}
                                            onClick={handleDelete}
                                            disabled={
                                                deleteResult.isLoading ||
                                                deleteResult.isSuccess
                                            }
                                        >
                                            <DeleteSvg
                                                width={16}
                                                height={16}
                                            />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        <textarea
                            ref={textareaRef}
                            className={styles.commentTextarea}
                            value={value}
                            onChange={handleChange}
                            onBlur={save}
                            placeholder={t("Action.WriteAComment")}
                        />
                    </div>
                </div>
            </section>
        </li>
    );
};
